import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "top-container" }
const _hoisted_2 = { class: "search-container" }
const _hoisted_3 = { class: "include-child-object-container" }
const _hoisted_4 = { class: "table-content" }
const _hoisted_5 = {
  key: 0,
  class: "selected-files"
}
const _hoisted_6 = {
  key: 2,
  class: "bottom-container__batch-actions"
}
const _hoisted_7 = {
  key: 0,
  class: "file-info-container"
}

import { ref, computed, onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { useGeneralStore } from '@/pinia'
import ButtonOutline from '@/design-system/atoms/buttons/ButtonOutline.vue'
import CheckBoxStandard from '@/design-system/atoms/CheckBoxStandard.vue'
import FileInfo from '@/components/features/attachments/FileInfo.vue'
import DividerHorizontal from '@/design-system/atoms/DividerHorizontal.vue'
import TableFilesLarge from '@/components/features/attachments/TableFilesLarge.vue'
import TableFilesMini from '@/components/features/attachments/TableFilesMini.vue'
import InfoLabel from '@/design-system/molecules/InfoLabel.vue'
import InputSearch from '@/design-system/molecules/InputSearch.vue'
import OverlayCard from '@/design-system/molecules/OverlayCard.vue'
import PaginationGroupCustom from '@/design-system/molecules/PaginationGroupCustom.vue'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import TooltipStandardV2 from '@/design-system/atoms/TooltipStandardV2.vue'
import ConfirmDeleteAttachmentCard from '@/components/features/attachments/ConfirmDeleteAttachmentCard.vue'
import {
  getAttachmentsPage,
  AttachmentsPage,
  archiveAttachments,
  deleteAttachments,
  Attachment,
  Direction
} from '@/services/attachments.service'
import { getAttachmentFileBlob } from '@/services/files.service'
import { debounce } from '@/utils/debounce'

type Emits = {
  (e: 'closeDashboard'): void
}

export default /*@__PURE__*/_defineComponent({
  __name: 'FilesDashboard',
  emits: ["closeDashboard"],
  setup(__props) {


const { t } = useI18n()
const piniaStore = useGeneralStore()
const store = useStore()

const filesList = computed(() => {
  return piniaStore.filesList
})
const isFeatureFullscreen = computed(() => {
  return piniaStore.isFeatureFullscreen
})
const selectedFile = computed(() => {
  return piniaStore.selectedFile
})
const selectedObject = computed(() => {
  return store.getters.selectedObject
})

const clearInput = ref(false)
const filesPage = ref<AttachmentsPage<Attachment>>()
const includeChildObject = ref(false)
const isLoadingFirstPage = ref(false)
const pageNumber = ref(0)
const resetSorting = ref(false)
const searchString = ref('')
const selectedFiles = ref<Attachment[]>([])
const showConfirmDelete = ref(false)
const sortBy = ref<string>('name')
const sortByOrder = ref<Direction>('asc')
const tableFilesLarge = ref()

const fields = computed(() => {
  const fields =
    !isFeatureFullscreen.value || mobileScreen.value
      ? [
          {
            key: 'name',
            name: filesPage.value
              ? filesPage.value.totalElements + ' ' + t('general__label__files').toLocaleLowerCase()
              : ''
          }
        ]
      : [
          {
            key: 'name',
            name: t('general__text__name'),
            sortable: true
          },
          {
            key: 'actType',
            name: t('general__text__type'),
            sortable: true
          },
          {
            key: 'actStatus',
            name: t('general__text__status'),
            sortable: true
          },
          {
            key: 'createdDate',
            name: t('general__text__uploaded'),
            sortable: true
          },
          {
            key: 'placement',
            name: t('general__text__placement')
          }
        ]
  return fields
})
const filesUploaded = computed(() => {
  return piniaStore.filesUploaded
})
const isSearchActive = computed(() => {
  return searchString.value.length >= 1
})
const mobileScreen = computed(() => {
  return store.state.mobileScreen
})
async function archiveFiles() {
  if (!selectedFile.value && !selectedFiles.value.length) return
  const archiveSingleFile = selectedFile.value || selectedFiles.value.length === 1
  try {
    const ids = selectedFile.value
      ? [selectedFile.value.attachmentId]
      : selectedFiles.value.map((file) => file.attachmentId)
    if (!ids.length) return
    const response = await archiveAttachments(ids)
    if (Array.isArray(response?.data.succeeded) && response?.data.succeeded.length) {
      if (isFeatureFullscreen.value && !mobileScreen.value) {
        // get page to refresh list in fullscreen
        if (filesList.value.length <= response.data.succeeded.length) {
          getPage(0)
        } else {
          getPage(pageNumber.value)
        }
      } else {
        // remove file from filesList in store
        const filesArray = filesList.value.filter(
          (file) =>
            !response.data.succeeded.find(
              (succeededId: string) => succeededId === file.attachmentId
            )
        )
        piniaStore.setFilesList(filesArray)
      }
      // remove selected files from selectedFiles array (in large file dashboard)
      removeFilesFromSelection(response.data.succeeded)
    }
    if (
      !response?.data.success &&
      Object.keys(response?.data.failed) &&
      Array.isArray(response?.data.succeeded) &&
      response?.data.succeeded.length
    ) {
      store.commit('setInfoMessage', {
        type: 'warning',
        message: `${t('general__data__files-archive-some-failed')}, ${t(
          'general__text__retry-later-again'
        ).toLowerCase()}`
      })
    } else if (
      !response?.data.success &&
      Object.keys(response?.data.failed) &&
      Array.isArray(response?.data.succeeded) &&
      !response?.data.succeeded.length
    ) {
      store.commit('setInfoMessage', {
        type: 'error',
        message: `${
          archiveSingleFile
            ? t('general__data__file-archive-failed')
            : t('general__data__files-archive-failed')
        }, ${t('general__text__retry-later-again').toLowerCase()}`
      })
    }
  } catch (e) {
    store.commit('setInfoMessage', {
      type: 'error',
      message: `${
        archiveSingleFile
          ? t('general__data__file-archive-failed')
          : t('general__data__files-archive-failed')
      }, ${t('general__text__retry-later-again').toLowerCase()}`
    })
    console.error(e)
  }
}
async function deleteFiles() {
  if (!selectedFile.value && !selectedFiles.value.length) return
  const deleteSingleFile = selectedFile.value || selectedFiles.value.length === 1
  try {
    const ids = selectedFile.value
      ? [selectedFile.value.attachmentId]
      : selectedFiles.value.map((file) => file.attachmentId)
    if (!ids.length) return
    const response = await deleteAttachments(ids)
    if (Array.isArray(response?.data.succeeded) && response?.data.succeeded.length) {
      if (isFeatureFullscreen.value && !mobileScreen.value) {
        // get page to refresh list in fullscreen
        if (filesList.value.length <= response.data.succeeded.length) {
          getPage(0)
        } else {
          getPage(pageNumber.value)
        }
      } else {
        // remove file from filesList in store
        const filesArray = filesList.value.filter(
          (file) =>
            !response.data.succeeded.find(
              (succeededId: string) => succeededId === file.attachmentId
            )
        )
        piniaStore.setFilesList(filesArray)
      }
      // remove selected files from selectedFiles array (in large file dashboard)
      removeFilesFromSelection(response.data.succeeded)
    }
    if (
      !response?.data.success &&
      Object.keys(response?.data.failed) &&
      Array.isArray(response?.data.succeeded) &&
      response?.data.succeeded.length
    ) {
      store.commit('setInfoMessage', {
        type: 'warning',
        message: `${t('general__data__files-delete-some-failed')}, ${t(
          'general__text__retry-later-again'
        ).toLowerCase()}`
      })
    } else if (
      !response?.data.success &&
      Object.keys(response?.data.failed) &&
      Array.isArray(response?.data.succeeded) &&
      !response?.data.succeeded.length
    ) {
      store.commit('setInfoMessage', {
        type: 'error',
        message: `${
          deleteSingleFile
            ? t('general__data__file-delete-failed')
            : t('general__data__files-delete-failed')
        }, ${t('general__text__retry-later-again').toLowerCase()}`
      })
    }
  } catch (e) {
    store.commit('setInfoMessage', {
      type: 'error',
      message: `${
        deleteSingleFile
          ? t('general__data__file-delete-failed')
          : t('general__data__files-delete-failed')
      }, ${t('general__text__retry-later-again').toLowerCase()}`
    })
    console.error(e)
  }
}

function downloadFiles() {
  if (!selectedFile.value && !selectedFiles.value.length) return
  const downloadSingleFile = selectedFile.value || selectedFiles.value.length === 1
  async function getFile(file: Attachment) {
    try {
      const res = await getAttachmentFileBlob(file)
      if (!res) return setDownloadFailedMessage(downloadSingleFile)
      // @ts-ignore
      const fileUrl = window.URL.createObjectURL(new Blob([res.data]))
      const fileLink = document.createElement('a')
      fileLink.href = fileUrl
      fileLink.setAttribute('download', file.name)
      document.body.appendChild(fileLink)
      fileLink.click()
    } catch (e) {
      setDownloadFailedMessage(downloadSingleFile)
      console.error(e)
    }
  }
  if (selectedFile.value && !selectedFiles.value.length) {
    getFile(selectedFile.value)
  } else {
    for (const file of selectedFiles.value) {
      getFile(file)
    }
  }
}
async function getItems(
  entityId: string,
  page: number,
  fullscreen: boolean,
  sortBy?: string,
  order?: Direction
) {
  const filesArray: Attachment[] = fullscreen ? [] : piniaStore.filesList
  let sortingBy = fullscreen && sortBy !== 'name' ? sortBy : 'nameLower' // nameLower instead of name handles upper case and lower case sorting correctly
  const sortingOrder = fullscreen ? order : 'asc'
  const sort = `${sortingBy},${sortingOrder}`
  filesPage.value = await getAttachmentsPage(
    entityId,
    ['FILE', 'ACT'],
    page,
    fullscreen ? 10 : 25,
    sort,
    null,
    includeChildObject.value
  )
  filesPage.value?.content.forEach((item) => {
    filesArray.push(item)
  })
  piniaStore.setFilesList(filesArray)
}
async function getSearchItems(
  entityId: string,
  searchFilter: string,
  page: number,
  fullscreen: boolean,
  sortBy?: string,
  order?: Direction
) {
  const filesArray: Attachment[] = fullscreen ? [] : piniaStore.filesList
  const sortingBy = fullscreen && sortBy !== 'name' ? sortBy : 'nameLower' // nameLower instead of name handles upper case and lower case sorting correctly
  const sortingOrder = fullscreen ? order : 'asc'
  const sort = `${sortingBy},${sortingOrder}`
  filesPage.value = await getAttachmentsPage(
    entityId,
    ['FILE', 'ACT'],
    page,
    fullscreen ? 10 : 25,
    sort,
    searchFilter,
    includeChildObject.value
  )
  filesPage.value?.content.forEach((item) => {
    filesArray.push(item)
  })
  piniaStore.setFilesList(filesArray)
}
function loadMoreFiles() {
  pageNumber.value++
  if (pageNumber.value === filesPage.value?.totalPages) {
    return
  }
  if (isSearchActive.value) {
    getSearchItems(store.getters.selectedObjectId, searchString.value, pageNumber.value, false)
  } else {
    getItems(store.getters.selectedObjectId, pageNumber.value, false)
  }
}
function getPage(page: number) {
  pageNumber.value = page
  if (isSearchActive.value) {
    getSearchItems(
      store.getters.selectedObjectId,
      searchString.value,
      pageNumber.value,
      true,
      sortBy.value,
      sortByOrder.value
    )
  } else {
    getItems(
      store.getters.selectedObjectId,
      pageNumber.value,
      true,
      sortBy.value,
      sortByOrder.value
    )
  }
}
function getNextPage() {
  pageNumber.value++
  getPage(pageNumber.value)
}
function getPreviousPage() {
  pageNumber.value--
  if (pageNumber.value < 0) {
    pageNumber.value = 0
  }
  getPage(pageNumber.value)
}
function refreshList() {
  resetSorting.value = true
  sortBy.value = 'name'
  sortByOrder.value = 'asc'
  if (isSearchActive.value) {
    resetAndSearchFiles(searchString.value)
  } else {
    resetAndGetFileList()
  }
}
const refreshListDebounce = debounce(() => {
  resetSorting.value = true
  sortBy.value = 'name'
  sortByOrder.value = 'asc'
  if (isSearchActive.value) {
    resetAndSearchFiles(searchString.value)
  } else {
    resetAndGetFileList()
  }
}, 400)
function removeFilesFromSelection(succeededFiles: string[]) {
  const selectedFilesArray = selectedFiles.value.filter(
    (file) => !succeededFiles.find((succeededId: string) => succeededId === file.attachmentId)
  )
  if (selectedFilesArray.length === 1) {
    piniaStore.setSelectedFile(selectedFilesArray[0])
  } else {
    piniaStore.setSelectedFile(null)
  }
  setSelectedFiles(selectedFilesArray)
}
async function resetAndGetFileList() {
  if (!selectedObject.value && selectedObject.value !== 0) return
  if (!Object.keys(selectedObject.value).length && selectedObject.value !== 0) return
  piniaStore.setFilesList([])
  pageNumber.value = 0
  isLoadingFirstPage.value = true
  await getItems(
    store.getters.selectedObjectId,
    pageNumber.value,
    isFeatureFullscreen.value,
    sortBy.value,
    sortByOrder.value
  )
  isLoadingFirstPage.value = false
}
async function resetAndSearchFiles(newVal: string) {
  if (!selectedObject.value && selectedObject.value !== 0) return
  if (!Object.keys(selectedObject.value).length && selectedObject.value !== 0) return
  piniaStore.setFilesList([])
  pageNumber.value = 0
  isLoadingFirstPage.value = true
  await getSearchItems(
    store.getters.selectedObjectId,
    newVal,
    0,
    isFeatureFullscreen.value,
    sortBy.value,
    sortByOrder.value
  )
  isLoadingFirstPage.value = false
}
function resetSelections() {
  tableFilesLarge.value?.resetSelections()
}
function searchStringValue(searchValue: string) {
  if (searchValue.length >= 1) clearInput.value = false
  searchString.value = searchValue
}
function setDownloadFailedMessage(downloadSingleFile) {
  store.commit('setInfoMessage', {
    type: 'error',
    message: `${
      downloadSingleFile
        ? t('general__data__file-download-failed')
        : t('general__data__files-download-failed')
    }, ${t('general__text__retry-later-again').toLowerCase()}`
  })
}
function setSelectedFiles(selectedFilesArray: Attachment[] = selectedFiles.value) {
  if (tableFilesLarge.value && isFeatureFullscreen.value) {
    if (selectedFile.value) {
      tableFilesLarge.value.setSelectedFilesFromDashboard([selectedFile.value])
    } else {
      tableFilesLarge.value.setSelectedFilesFromDashboard(selectedFilesArray)
    }
  }
}
function showModalUploadFiles() {
  clearInput.value = true
  piniaStore.setCurrentOverlayModalPinia('uploadFilesPinia')
  piniaStore.setShowOverlayModalPinia(true)
}
function sortByType(key: string, order: Direction) {
  resetSorting.value = false
  sortBy.value = key
  sortByOrder.value = order
  if (!sortBy.value) return
  if (isSearchActive.value) {
    getSearchItems(
      store.getters.selectedObjectId,
      searchString.value,
      pageNumber.value,
      true,
      sortBy.value,
      sortByOrder.value
    )
  } else {
    getItems(
      store.getters.selectedObjectId,
      pageNumber.value,
      true,
      sortBy.value,
      sortByOrder.value
    )
  }
}
watch(filesList, (newList, oldList) => {
  // remove one item from list when a file deleted
  if (!filesPage.value?.totalElements) return
  // Todo: The line below makes the totalElements value incorrect for the last page
  if (newList.length < oldList.length) filesPage.value.totalElements--
})
watch(
  filesUploaded,
  (newFilesArray, oldFilesArray) => {
    if (newFilesArray.length && newFilesArray.length > oldFilesArray.length) {
      piniaStore.setFilesList([])
      pageNumber.value = 0
      getItems(
        store.getters.selectedObjectId,
        pageNumber.value,
        isFeatureFullscreen.value,
        sortBy.value,
        sortByOrder.value
      )
    }
  },
  { deep: true }
)
watch(includeChildObject, (newVal, oldVal) => {
  if (newVal === false && oldVal === true) {
    piniaStore.setSelectedFile(null)
    setSelectedFiles([])
  }
  refreshList()
})
watch(searchString, (newVal, oldVal) => {
  if (newVal !== oldVal) refreshListDebounce()
})
watch(selectedFile, (newFile) => {
  if (!isFeatureFullscreen.value || mobileScreen.value) {
    if (!newFile) selectedFiles.value = []
  }
})
watch(
  selectedObject,
  async () => {
    // Don't include child objects for realm
    if (selectedObject.value === 0) includeChildObject.value = false
    refreshList()
    piniaStore.setSelectedFile(null)
  },
  { immediate: true }
)
watch(isFeatureFullscreen, () => {
  refreshList()
})
watch(tableFilesLarge, () => {
  setSelectedFiles()
})
onUnmounted(() => {
  piniaStore.setSelectedFile(null)
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    id: "files-dashboard",
    class: _normalizeClass({
      fullscreen: isFeatureFullscreen.value && !mobileScreen.value,
      mobile: mobileScreen.value
    })
  }, [
    (showConfirmDelete.value)
      ? (_openBlock(), _createBlock(OverlayCard, {
          key: 0,
          class: _normalizeClass(["overlay-confirm-delete", {
        'overlay-confirm-delete__mobile': mobileScreen.value,
        'overlay-confirm-delete__fullscreen': isFeatureFullscreen.value && !mobileScreen.value
      }]),
          onCloseOverlay: _cache[2] || (_cache[2] = ($event: any) => (showConfirmDelete.value = false))
        }, {
          default: _withCtx(() => [
            _createVNode(ConfirmDeleteAttachmentCard, {
              buttonText: _unref(t)('general__text__delete') + ' ' + _unref(t)('general__data__file(s)').toLowerCase(),
              onCloseOverlay: _cache[0] || (_cache[0] = ($event: any) => (showConfirmDelete.value = false)),
              onConfirmDelete: _cache[1] || (_cache[1] = ($event: any) => (deleteFiles()))
            }, null, 8, ["buttonText"])
          ]),
          _: 1
        }, 8, ["class"]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createVNode(InputSearch, {
          class: "search-container__input",
          clearInput: clearInput.value,
          placeholderText: _unref(t)('general__text__search-placeholder-files'),
          onEmitSearchString: searchStringValue
        }, null, 8, ["clearInput", "placeholderText"])
      ]),
      _createElementVNode("div", _hoisted_3, [
        _createVNode(CheckBoxStandard, {
          class: "include-child-object-container__checkbox",
          checked: includeChildObject.value,
          disabled: selectedObject.value === 0,
          title: _unref(t)('general__text__objects-include-child'),
          onValueChanged: _cache[3] || (_cache[3] = ($event: any) => (includeChildObject.value = !includeChildObject.value))
        }, null, 8, ["checked", "disabled", "title"]),
        _createVNode(InfoLabel, {
          class: "include-child-object-container__info",
          info: _unref(t)('tooltip__files-include-child-objects')
        }, null, 8, ["info"])
      ])
    ]),
    (isFeatureFullscreen.value && !mobileScreen.value)
      ? (_openBlock(), _createBlock(DividerHorizontal, {
          key: 1,
          style: {"margin":"8px 0 0"}
        }))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_4, [
      _createElementVNode("div", {
        class: _normalizeClass(["table-container", { 'table-container__preview': selectedFile.value }])
      }, [
        _createVNode(PulseLoader, {
          loading: isLoadingFirstPage.value,
          class: _normalizeClass(["spinner", {
            spinner__fullscreen: isFeatureFullscreen.value && !mobileScreen.value,
            spinner__mobile: mobileScreen.value
          }]),
          color: "#1d234f"
        }, null, 8, ["loading", "class"]),
        (!isLoadingFirstPage.value && (!isFeatureFullscreen.value || mobileScreen.value))
          ? (_openBlock(), _createBlock(TableFilesMini, {
              key: 0,
              fields: fields.value,
              items: filesList.value,
              onArchiveFile: archiveFiles,
              onDeleteFile: _cache[4] || (_cache[4] = ($event: any) => (showConfirmDelete.value = true)),
              onDownloadFile: downloadFiles,
              onLoadMoreFiles: loadMoreFiles
            }, null, 8, ["fields", "items"]))
          : (!isLoadingFirstPage.value)
            ? (_openBlock(), _createBlock(TableFilesLarge, {
                key: 1,
                ref_key: "tableFilesLarge",
                ref: tableFilesLarge,
                class: "table-large",
                fields: fields.value,
                items: filesList.value,
                resetSorting: resetSorting.value,
                onArchiveFile: archiveFiles,
                onDeleteFile: _cache[5] || (_cache[5] = ($event: any) => (showConfirmDelete.value = true)),
                onDownloadFile: downloadFiles,
                onSelectedFiles: _cache[6] || (_cache[6] = ($event: any) => (selectedFiles.value = $event)),
                onSortBy: sortByType
              }, null, 8, ["fields", "items", "resetSorting"]))
            : _createCommentVNode("", true),
        _createElementVNode("div", {
          class: _normalizeClass(["bottom-container", { 'bottom-container__no-button': !_unref(store).getters.hasFileCreatorAccess }])
        }, [
          (isFeatureFullscreen.value && !mobileScreen.value && selectedFiles.value.length >= 2)
            ? (_openBlock(), _createElementBlock("p", _hoisted_5, _toDisplayString(selectedFiles.value.length + ' ' + _unref(t)('general__data__files-selected').toLowerCase()), 1))
            : _createCommentVNode("", true),
          (
              (_unref(store).getters.hasFileCreatorAccess && selectedFiles.value.length <= 1) ||
              (_unref(store).getters.hasFileCreatorAccess && !isFeatureFullscreen.value) ||
              (_unref(store).getters.hasFileCreatorAccess && mobileScreen.value)
            )
            ? (_openBlock(), _createBlock(ButtonOutline, {
                key: 1,
                class: "bottom-container__button",
                buttonText: _unref(t)('general__data__file-upload-a'),
                iconLeft: require('@/design-system/assets/icons/Icon-Upload-Small.svg'),
                size: 'small',
                onOnClicked: showModalUploadFiles
              }, null, 8, ["buttonText", "iconLeft"]))
            : _createCommentVNode("", true),
          (isFeatureFullscreen.value && !mobileScreen.value && selectedFiles.value.length >= 2)
            ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                _createVNode(ButtonOutline, {
                  size: "small",
                  buttonText: _unref(t)('general__text__dashboard_reset_selections'),
                  onOnClicked: resetSelections
                }, null, 8, ["buttonText"]),
                _createVNode(TooltipStandardV2, {
                  text: _unref(t)('general__text__download'),
                  arrowPosition: 'bottom-center',
                  verticalOffset: 2
                }, {
                  default: _withCtx(() => [
                    _createVNode(ButtonOutline, {
                      class: "button",
                      iconLeft: require('@/design-system/assets/icons/Icon-Download-small.svg'),
                      size: 'small',
                      onOnClicked: downloadFiles
                    }, null, 8, ["iconLeft"])
                  ]),
                  _: 1
                }, 8, ["text"]),
                (_unref(store).getters.hasFileEditorAccess)
                  ? (_openBlock(), _createBlock(TooltipStandardV2, {
                      key: 0,
                      text: _unref(t)('general__text__archive'),
                      arrowPosition: 'bottom-center',
                      verticalOffset: 2
                    }, {
                      default: _withCtx(() => [
                        _createVNode(ButtonOutline, {
                          class: "button",
                          iconLeft: require('@/design-system/assets/icons/Icon-Archive-Small.svg'),
                          size: 'small',
                          onOnClicked: archiveFiles
                        }, null, 8, ["iconLeft"])
                      ]),
                      _: 1
                    }, 8, ["text"]))
                  : _createCommentVNode("", true),
                (_unref(store).getters.hasFileAdminAccess)
                  ? (_openBlock(), _createBlock(TooltipStandardV2, {
                      key: 1,
                      text: _unref(t)('general__text__delete'),
                      arrowPosition: 'bottom-center',
                      verticalOffset: 2
                    }, {
                      default: _withCtx(() => [
                        _createVNode(ButtonOutline, {
                          class: "button",
                          iconLeft: require('@/design-system/assets/icons/Icon-Trash-2-small.svg'),
                          size: 'small',
                          onOnClicked: _cache[7] || (_cache[7] = ($event: any) => (showConfirmDelete.value = true))
                        }, null, 8, ["iconLeft"])
                      ]),
                      _: 1
                    }, 8, ["text"]))
                  : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true),
          (isFeatureFullscreen.value && !mobileScreen.value)
            ? (_openBlock(), _createBlock(PaginationGroupCustom, {
                key: 3,
                class: "bottom-container__pagination",
                attachmentsPage: filesPage.value,
                onBack: getPreviousPage,
                onNext: getNextPage,
                onPage: getPage
              }, null, 8, ["attachmentsPage"]))
            : _createCommentVNode("", true)
        ], 2)
      ], 2),
      (isFeatureFullscreen.value && !mobileScreen.value && selectedFile.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
            _createVNode(FileInfo, {
              onArchiveFile: archiveFiles,
              onDeleteFile: _cache[8] || (_cache[8] = ($event: any) => (showConfirmDelete.value = true)),
              onDownloadFile: downloadFiles
            })
          ]))
        : _createCommentVNode("", true)
    ])
  ], 2))
}
}

})